import React from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material'
import dayjs from 'dayjs'
import { AppointmentHeader } from './AppointmentHeader'
import { AppointmentContent } from './AppointmentContent'
import { EmptyState } from '../../../../../common/components/EmptyState'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import { Appointment } from '../../types/treatments.types'
import { useDialogStyles } from '../../../../../utils'

interface AppointmentModal {
  appointment?: Appointment
  isOpen: boolean | string
  onClose: () => void
  editAppointment: (appointment: Appointment) => void
  cloneAppointment: (appointment: Appointment) => void
}

const AppointmentModal: React.FC<React.PropsWithChildren<AppointmentModal>> = ({
  appointment,
  isOpen,
  onClose,
  editAppointment,
  cloneAppointment,
}) => {
  const dialogClasses = useDialogStyles()
  const { t } = useTranslation()

  const isPast = dayjs().startOf('day') > dayjs(appointment?.proposedDate)

  return (
    <Dialog
      open={!!isOpen}
      classes={dialogClasses}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose()
        }
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogContent>
        {!appointment && (
          <Box
            minHeight={360}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <EmptyState
              title={t('appointment:patient.invalidAppointmentMessageTitle')}
              description={t(
                'appointment:patient.invalidAppointmentMessageContent'
              )}
            />
          </Box>
        )}
        {appointment && (
          <>
            <AppointmentHeader
              isOpen
              appointment={appointment}
              isPast={isPast}
              editAppointment={() => editAppointment(appointment)}
              cloneAppointment={() => cloneAppointment(appointment)}
            />
            <AppointmentContent
              isPast={isPast}
              appointment={appointment}
              onDeleteComplete={onClose}
              onEditLocation={() => editAppointment(appointment)}
              onDeleteProtocolComplete={onClose}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={onClose}>
          {t('common:close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export { AppointmentModal }
